import { useMemo } from 'react'
import {
  useVerticalConfig,
  useFeatureEnabled,
  usePermission,
} from '@lipaflow/shared'
import {
  HomeIcon,
  UsersIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  MegaphoneIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ShieldExclamationIcon,
  ChartBarSquareIcon,
  EyeIcon,
  TableCellsIcon,
  LightBulbIcon,
  CalendarDaysIcon,
  BookOpenIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline'

export type NavChild = {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  keywords?: string[]
}

export type NavItem = {
  name: string
  href?: string
  icon?: React.ComponentType<{ className?: string }>
  children?: NavChild[]
  keywords?: string[]
}

function pluralize(s: string) {
  if (s.endsWith('y')) return s.slice(0, -1) + 'ies'
  if (s.endsWith('s') || s.endsWith('x') || s.endsWith('ch')) return s + 'es'
  return s + 's'
}

export function useNavigation() {
  const { labels } = useVerticalConfig()
  const hasAcademics = useFeatureEnabled('academics')
  const hasLeases = useFeatureEnabled('leases')
  const hasPropertyManagement = useFeatureEnabled('property_management')
  const canViewAudit = usePermission('audit:view')
  const canViewSettings = usePermission('settings:view')
  const canViewBeneficiaries = usePermission('beneficiaries:view')
  const canViewAttendance = usePermission('attendance:view')
  const canViewAssignments = usePermission('assignments:view')
  const canViewDiscipline = usePermission('discipline:view')
  const canViewTimetable = usePermission('timetable:view')
  const canManageTimetable = usePermission('timetable:manage')
  const canViewResults = usePermission('results:view')
  const canViewClasses = usePermission('classes:view')
  const canViewClaims = usePermission('claims:view')
  const canViewPlans = usePermission('plans:view')
  const canUploadBalance = usePermission('balance:upload')
  const canViewPledges = usePermission('pledges:view')
  const canViewReports = usePermission('reports:view')
  const canViewFeeStructures = usePermission('fee_structures:view')
  const canViewEscalations = usePermission('escalations:view')
  const canViewSegments = usePermission('segments:view')
  const canViewSessions = usePermission('sessions:view')
  const canViewBroadcasts = usePermission('broadcasts:view')

  const entityIcon = hasAcademics ? AcademicCapIcon : BuildingOfficeIcon
  const entityLabelPlural = pluralize(labels.entity)
  const groupLabelPlural = pluralize(labels.group)

  const navigation = useMemo<NavItem[]>(() => {
    const peopleChildren: NavChild[] = []
    if (canViewBeneficiaries) {
      peopleChildren.push({
        name: entityLabelPlural,
        href: '/entities',
        icon: entityIcon,
        keywords: ['students', 'learners', 'tenants', 'beneficiaries'],
      })
    }
    if (hasAcademics) {
      if (canViewAttendance) {
        peopleChildren.push({ name: 'Attendance', href: '/attendance', icon: CalendarDaysIcon, keywords: ['roll call', 'register', 'present', 'absent'] })
      }
      if (canViewTimetable) {
        peopleChildren.push({ name: 'Timetable', href: '/timetable', icon: CalendarDaysIcon, keywords: ['schedule', 'lessons', 'periods'] })
      }
      if (canViewResults) {
        peopleChildren.push({ name: 'Results', href: '/results', icon: DocumentChartBarIcon, keywords: ['grades', 'marks', 'exams', 'scores'] })
      }
      if (canViewAssignments) {
        peopleChildren.push({ name: 'Assignments', href: '/assignments', icon: BookOpenIcon, keywords: ['homework', 'tasks'] })
      }
      if (canViewDiscipline) {
        peopleChildren.push({ name: 'Discipline', href: '/discipline', icon: HandRaisedIcon, keywords: ['incidents', 'conduct', 'behavior'] })
      }
      if (canViewClasses) {
        peopleChildren.push({ name: 'Classroom', href: '/classroom', icon: AcademicCapIcon, keywords: ['class', 'room'] })
      }
      if (canViewTimetable) {
        peopleChildren.push({ name: 'Curriculum', href: '/curriculum', icon: BookOpenIcon, keywords: ['syllabus', 'CBC', 'strands', 'outcomes'] })
        peopleChildren.push({ name: 'Planning', href: '/planning', icon: ClipboardDocumentListIcon, keywords: ['lesson plans', 'scheme of work', 'copilot'] })
        if (canManageTimetable) {
          peopleChildren.push({ name: 'Assessment', href: '/assessment', icon: ClipboardDocumentCheckIcon, keywords: ['questions', 'question bank', 'rubric', 'exam'] })
          peopleChildren.push({ name: 'KNEC Alignment', href: '/knec-alignment', icon: ChartBarSquareIcon, keywords: ['knec', 'exam', 'gap analysis', 'past papers'] })
        }
      }
    }
    if (canViewBeneficiaries) {
      peopleChildren.push({
        name: groupLabelPlural,
        href: '/clients',
        icon: UsersIcon,
        keywords: ['parents', 'guardians', 'families', 'groups'],
      })
    }

    const financeChildren: NavChild[] = []
    if (canViewSettings) {
      financeChildren.push({ name: 'Subscriptions', href: '/subscriptions', icon: BanknotesIcon, keywords: ['plan', 'renewal', 'invoice', 'billing'] })
    }
    if (canUploadBalance) {
      financeChildren.push({ name: 'Reconciliation', href: '/balance-upload', icon: DocumentChartBarIcon, keywords: ['upload', 'balance', 'import'] })
    }
    if (canViewClaims) {
      financeChildren.push({ name: 'Payment Claims', href: '/claims', icon: ClipboardDocumentCheckIcon, keywords: ['payments', 'receipts'] })
    }
    if (canViewPlans) {
      financeChildren.push({ name: 'Payment Plans', href: '/payment-plans', icon: BanknotesIcon, keywords: ['installments', 'schedules'] })
    }
    if (canViewPledges) {
      financeChildren.push({ name: 'Pledges', href: '/pledges', icon: ShieldExclamationIcon, keywords: ['commitments', 'promises'] })
    }
    if (canViewReports) {
      financeChildren.push({ name: 'Reports', href: '/reports', icon: ChartBarSquareIcon, keywords: ['analytics', 'collection', 'aging'] })
      financeChildren.push({ name: 'Insights', href: '/insights', icon: LightBulbIcon, keywords: ['trends', 'analysis'] })
    }
    if (hasAcademics && canViewFeeStructures) {
      financeChildren.push({ name: 'Fee Structures', href: '/fee-structures', icon: TableCellsIcon, keywords: ['fees', 'tuition', 'pricing'] })
    }
    if (hasLeases) {
      financeChildren.push({ name: 'Leases', href: '/leases', icon: DocumentTextIcon, keywords: ['rental', 'contracts', 'units'] })
    }
    if (hasPropertyManagement) {
      financeChildren.push({ name: 'Maintenance', href: '/maintenance', icon: WrenchScrewdriverIcon, keywords: ['repairs', 'requests', 'tickets'] })
    }

    const items: NavItem[] = [
      { name: 'Dashboard', href: '/', icon: HomeIcon, keywords: ['home', 'overview'] },
    ]
    if (peopleChildren.length > 0) {
      items.push({ name: 'People', children: peopleChildren })
    }
    if (financeChildren.length > 0) {
      items.push({ name: 'Finance', children: financeChildren })
    }
    const communicationChildren: NavChild[] = []
    if (canViewSessions) {
      communicationChildren.push({ name: 'Conversations', href: '/conversations', icon: ChatBubbleLeftRightIcon, keywords: ['chat', 'whatsapp', 'messages'] })
    }
    if (canViewBroadcasts) {
      communicationChildren.push({ name: 'Broadcasts', href: '/broadcasts', icon: MegaphoneIcon, keywords: ['bulk', 'sms', 'notifications'] })
      communicationChildren.push({ name: 'Templates', href: '/templates', icon: DocumentTextIcon, keywords: ['message templates'] })
      if (hasAcademics) {
        communicationChildren.push({ name: 'Announcements', href: '/announcements', icon: MegaphoneIcon, keywords: ['notices', 'alerts'] })
      }
    }
    if (communicationChildren.length > 0) {
      items.push({ name: 'Communication', children: communicationChildren })
    }
    if (canViewEscalations) {
      items.push({ name: 'Escalations', href: '/escalations', icon: ShieldExclamationIcon, keywords: ['follow up', 'risk', 'alerts'] })
    }
    if (canViewSegments) {
      items.push({ name: 'Segments', href: '/segments', icon: UsersIcon, keywords: ['grouping', 'cohorts'] })
    }
    if (canViewAudit) {
      items.push({ name: 'Audit Trail', href: '/audit-trail', icon: EyeIcon, keywords: ['logs', 'history', 'activity'] })
    }
    if (canViewSettings) {
      items.push({ name: 'Settings', href: '/settings', icon: Cog6ToothIcon, keywords: ['preferences', 'configuration'] })
    }
    return items
  }, [entityLabelPlural, groupLabelPlural, entityIcon, hasAcademics, hasLeases, hasPropertyManagement, canViewAudit, canViewSettings, canViewBeneficiaries, canViewAttendance, canViewAssignments, canViewDiscipline, canViewTimetable, canManageTimetable, canViewResults, canViewClasses, canViewClaims, canViewPlans, canUploadBalance, canViewPledges, canViewReports, canViewFeeStructures, canViewEscalations, canViewSegments, canViewSessions, canViewBroadcasts])

  return { navigation, labels, entityLabelPlural, groupLabelPlural, entityIcon }
}
